import {
  SortDisposition,
  SortDispositionSchema,
} from "@redotech/redo-fulfillment-wms-common/schema/sort-disposition";
import { readReturnsCenterId } from "@redotech/redo-return-core-common/returns-center-id";
import * as z from "zod";
import {
  DEFAULT_GRADE_MAPPINGS,
  GradeAcceptance,
  type GradeMapping,
} from "./grade-mapping";

const GradeSortDispositionOverrideSchema = z.object({
  gradeName: z.string(),
  sortDisposition: SortDispositionSchema,
});
const GradeSortDispositionOverridesSchema = z.array(
  GradeSortDispositionOverrideSchema,
);
export type GradeSortDispositionOverride = z.infer<
  typeof GradeSortDispositionOverrideSchema
>;

const RETURNS_CENTER_GRADE_OVERRIDES_PATH_PREFIX =
  "redoManagedReturns.returnsCenters.";

export function returnsCenterGradeOverridesPath(
  returnsCenterId: bigint,
): string {
  return `${RETURNS_CENTER_GRADE_OVERRIDES_PATH_PREFIX}${returnsCenterId}.gradeSortDispositions`;
}

const RETURNS_CENTER_GRADE_OVERRIDES_PATH_PATTERN =
  /^redoManagedReturns\.returnsCenters\.(\d+)\.gradeSortDispositions$/;

export function readReturnsCenterGradeOverridesPath(
  path: string,
): bigint | null {
  const returnsCenterId =
    RETURNS_CENTER_GRADE_OVERRIDES_PATH_PATTERN.exec(path)?.[1];
  return returnsCenterId ? BigInt(returnsCenterId) : null;
}

export function isReturnsCenterGradeOverridesPath(path: string): boolean {
  return readReturnsCenterGradeOverridesPath(path) !== null;
}

/** One center's overrides as RPCs carry them; `returnsCenterId` may be `rctr_` or the decoded id. */
export const ReturnsCenterGradeOverridesSchema = z.object({
  returnsCenterId: z.string(),
  gradeSortDispositions: GradeSortDispositionOverridesSchema,
});
export type ReturnsCenterGradeOverrides = z.infer<
  typeof ReturnsCenterGradeOverridesSchema
>;

function withSortDisposition(
  mapping: GradeMapping,
  sortDisposition: SortDisposition,
): GradeMapping {
  if (
    mapping.acceptance === GradeAcceptance.REJECT &&
    sortDisposition === SortDisposition.restock
  ) {
    return mapping;
  }
  return {
    gradeName: mapping.gradeName,
    acceptance: mapping.acceptance,
    restock: sortDisposition === SortDisposition.restock,
    sortDisposition,
  };
}

/** Account-wide mappings with a center's per-grade dispositions applied; acceptance stays global. */
export function gradeMappingsWithOverrides(
  gradeMappings: readonly GradeMapping[] | undefined,
  overrides: readonly GradeSortDispositionOverride[] | null | undefined,
): readonly GradeMapping[] | undefined {
  if (!overrides?.length) {
    return gradeMappings;
  }
  const base = gradeMappings?.length ? gradeMappings : DEFAULT_GRADE_MAPPINGS;
  const byGrade = new Map(overrides.map((o) => [o.gradeName, o]));
  const applied = Array.from(base, (mapping) => {
    const override = byGrade.get(mapping.gradeName);
    return override
      ? withSortDisposition(mapping, override.sortDisposition)
      : mapping;
  });
  const defaultsOverridden = DEFAULT_GRADE_MAPPINGS.flatMap((mapping) => {
    const override = byGrade.get(mapping.gradeName);
    return override && !base.some((m) => m.gradeName === mapping.gradeName)
      ? [withSortDisposition(mapping, override.sortDisposition)]
      : [];
  });
  return [...applied, ...defaultsOverridden];
}

/** Malformed stored overrides read as none, so the center follows the account-wide mappings. */
export function parseStoredGradeOverrides(
  raw: unknown,
): GradeSortDispositionOverride[] {
  if (raw == null) {
    return [];
  }
  const parsed = GradeSortDispositionOverridesSchema.safeParse(raw);
  return parsed.success ? parsed.data : [];
}

export function normalizeGradeOverrides(
  overrides: readonly GradeSortDispositionOverride[],
): GradeSortDispositionOverride[] {
  return overrides
    .map(({ gradeName, sortDisposition }) => ({ gradeName, sortDisposition }))
    .sort((a, b) => a.gradeName.localeCompare(b.gradeName));
}

export function gradeOverridesValidationErrors(
  overrides: readonly GradeSortDispositionOverride[],
): string[] {
  const seen = new Set<string>();
  const errors: string[] = [];
  for (const { gradeName } of overrides) {
    if (seen.has(gradeName)) {
      errors.push(`Grade ${gradeName} is overridden more than once`);
    }
    seen.add(gradeName);
  }
  return errors;
}

/** Unlike stored reads, a malformed or duplicated proposed value throws. */
export function parseGradeOverridesValue(
  value: unknown,
): GradeSortDispositionOverride[] {
  if (value == null) {
    return [];
  }
  const overrides = normalizeGradeOverrides(
    GradeSortDispositionOverridesSchema.parse(value),
  );
  const errors = gradeOverridesValidationErrors(overrides);
  if (errors.length > 0) {
    throw new Error(errors.join("; "));
  }
  return overrides;
}

/** Only centers in `stored` have a settings row to hold overrides, so others are skipped; omitted centers keep theirs. */
export function diffReturnsCenterGradeOverrides(
  stored: readonly ReturnsCenterGradeOverrides[],
  next: readonly ReturnsCenterGradeOverrides[] | null | undefined,
): {
  path: string;
  prev: GradeSortDispositionOverride[];
  next: GradeSortDispositionOverride[];
}[] {
  if (next == null) {
    return [];
  }
  const storedById = new Map(
    stored.flatMap((center) => {
      const id = readReturnsCenterId(center.returnsCenterId);
      return id === null ? [] : [[id, center] as const];
    }),
  );
  return next.flatMap((center) => {
    const id = readReturnsCenterId(center.returnsCenterId);
    const saved = id === null ? undefined : storedById.get(id);
    if (id === null || !saved) {
      return [];
    }
    const prevValue = normalizeGradeOverrides(saved.gradeSortDispositions);
    const nextValue = normalizeGradeOverrides(center.gradeSortDispositions);
    return JSON.stringify(prevValue) === JSON.stringify(nextValue)
      ? []
      : [
          {
            path: returnsCenterGradeOverridesPath(id),
            prev: prevValue,
            next: nextValue,
          },
        ];
  });
}
